/**
 * Order Overview — the label + health catalog.
 *
 * ONE home for what a label MEANS: its key, display name, user-facing
 * definition, kind, severity rank, and whether it flaps. Before this file the
 * concept was spread across five modules that each knew a piece — the census
 * SQL emitted the key, the webapp re-derived a display name with a regex, the
 * filter config re-validated the key with a character class, and the
 * definition existed only in a SQL comment — which is why the filter popover
 * showed "No Scope" while the table row showed "NoScope", and why no user
 * could see what any label actually meant.
 *
 * Lives in @birdwatch/common because the two stacks meet only here: webapp
 * must never import platform. Contracts are the sanctioned shared-downward
 * exception (coding.md Rule 14).
 *
 * WHAT THIS FILE DOES NOT OWN: the SQL predicates. Those stay in
 * platform/src/resources/report/order-overview/census.query.ts — how a label
 * is evaluated in Postgres is one module's secret (Rule 7). This catalog owns
 * semantics only.
 *
 * ANTI-DRIFT: platform/__tests__/resources/report/label-catalog.test.ts
 * extracts every `THEN '<Label>'` literal out of CENSUS_SQL and asserts the
 * emitted key set is EXACTLY this catalog's key set. Adding a label on either
 * side without the other fails the build.
 */

export type OrderOverviewLabelKind = "issue" | "motion" | "hygiene";

export interface OrderOverviewLabelMeta {
  /** Exact string literal emitted by CENSUS_SQL. */
  key: string;
  /** Human display name — replaces the old prettyLabel() regex. */
  displayName: string;
  /** One user-facing sentence. Rendered in the chip tooltip and filter list. */
  definition: string;
  /**
   * issue   — a defect worth a manager's attention; carries a severity rank.
   * motion  — describes recent movement, not a problem. Flaps by construction.
   * hygiene — data-quality bookkeeping, excluded from every health metric.
   */
  kind: OrderOverviewLabelKind;
  /** 1 = most severe. Null for motion/hygiene, which are not ranked. */
  severityRank: number | null;
  /**
   * True when the label's value changes on its own as time passes, so a
   * change in it is NOT evidence the order changed. Excluded from the
   * `label_change` AI-review trigger — otherwise every order re-reviews daily.
   */
  flaps: boolean;
  /** True when the label can never usefully appear as a filter option. */
  hiddenInFilters: boolean;
}

/**
 * The 17 labels, issues first in severity order, then motion, then hygiene.
 * Array order is meaningful: chips and filter options render in this order.
 */
export const ORDER_OVERVIEW_LABELS = [
  {
    key: "Flagged",
    displayName: "Flagged",
    definition: "A manager has flagged this order for attention.",
    kind: "issue",
    severityRank: 1,
    flaps: false,
    hiddenInFilters: false,
  },
  {
    key: "UnpaidAR",
    displayName: "Unpaid AR",
    definition:
      "An invoice has been sent and is still unpaid. Orders billed through the legacy invoice path are not detected.",
    kind: "issue",
    severityRank: 2,
    flaps: false,
    hiddenInFilters: false,
  },
  {
    key: "NegativeTone",
    displayName: "Negative Tone",
    definition:
      "The customer's most recent message reads as dissatisfied, frustrated, or urgent.",
    kind: "issue",
    severityRank: 3,
    flaps: false,
    hiddenInFilters: false,
  },
  {
    key: "Underwater",
    displayName: "Underwater",
    definition:
      "Logged spend exceeds booked revenue — the order is losing money as scoped.",
    kind: "issue",
    severityRank: 4,
    flaps: false,
    hiddenInFilters: false,
  },
  {
    key: "StalledCommitted",
    displayName: "Stalled",
    definition:
      "Committed work with no real activity — message, comment, or status change — for 14 or more days.",
    kind: "issue",
    severityRank: 5,
    flaps: false,
    hiddenInFilters: false,
  },
  {
    key: "MissingBilling",
    displayName: "Missing Billing",
    definition:
      "Sitting in Billing with no invoice raised, and not covered by a prepaid plan.",
    kind: "issue",
    severityRank: 6,
    flaps: false,
    hiddenInFilters: false,
  },
  {
    key: "CostNoRevenue",
    displayName: "Cost, No Revenue",
    definition: "Costs have been logged but no revenue is booked.",
    kind: "issue",
    severityRank: 7,
    flaps: false,
    hiddenInFilters: false,
  },
  {
    key: "ZeroMargin",
    displayName: "Zero Margin",
    definition:
      "Line items are priced exactly at cost — no margin is scoped into the order.",
    kind: "issue",
    severityRank: 8,
    flaps: false,
    hiddenInFilters: false,
  },
  {
    key: "ColdFunnel",
    displayName: "Cold Funnel",
    definition:
      "A pre-commitment order (new, reviewing, quoting, or waiting) with no activity for 30 or more days.",
    kind: "issue",
    severityRank: 9,
    flaps: false,
    hiddenInFilters: false,
  },
  {
    key: "ValueScopeDiverge",
    displayName: "Value / Scope Diverge",
    definition:
      "The headline order value and the scoped line-item total disagree by more than 5%.",
    kind: "issue",
    severityRank: 10,
    flaps: false,
    hiddenInFilters: false,
  },
  {
    key: "NoScope",
    displayName: "No Scope",
    definition:
      "Committed to the customer with nothing scoped — the order has zero line items.",
    kind: "issue",
    severityRank: 11,
    flaps: false,
    hiddenInFilters: false,
  },
  {
    key: "NoValue",
    displayName: "No Value",
    definition:
      "Committed with line items scoped, but no order value has been entered.",
    kind: "issue",
    severityRank: 12,
    flaps: false,
    hiddenInFilters: false,
  },
  {
    key: "ScopedButZero",
    displayName: "Scoped But Zero",
    definition:
      "The scope carries real value but the headline order value is still $0.",
    kind: "issue",
    severityRank: 13,
    flaps: false,
    hiddenInFilters: false,
  },
  {
    key: "CostIncomplete",
    displayName: "Cost Incomplete",
    definition: "One or more line items have no cost entered.",
    kind: "issue",
    severityRank: 14,
    flaps: false,
    hiddenInFilters: false,
  },
  {
    key: "New24h",
    displayName: "New 24h",
    definition: "Created in the last 24 hours.",
    kind: "motion",
    severityRank: null,
    flaps: true,
    hiddenInFilters: false,
  },
  {
    key: "Moved24h",
    displayName: "Moved 24h",
    definition:
      "Had real activity — a message, comment, or status change — in the last 24 hours.",
    kind: "motion",
    severityRank: null,
    flaps: true,
    hiddenInFilters: false,
  },
  {
    key: "QATest",
    displayName: "QA / Test",
    definition:
      "Test or QA data — kept in the book but excluded from every health metric.",
    kind: "hygiene",
    severityRank: null,
    flaps: false,
    // labelCounts is computed WHERE NOT "isQaTest", and isQaTest is exactly
    // this label — so it can never have a non-zero count to offer.
    hiddenInFilters: true,
  },
] as const satisfies readonly OrderOverviewLabelMeta[];

export type OrderOverviewLabel = (typeof ORDER_OVERVIEW_LABELS)[number]["key"];

const LABELS_BY_KEY = new Map<string, OrderOverviewLabelMeta>(
  ORDER_OVERVIEW_LABELS.map((label) => [label.key, label]),
);

/** Catalog entry for a key, or undefined for a key this build doesn't know. */
export function getOrderOverviewLabel(
  key: string,
): OrderOverviewLabelMeta | undefined {
  return LABELS_BY_KEY.get(key);
}

export function isOrderOverviewLabel(key: string): key is OrderOverviewLabel {
  return LABELS_BY_KEY.has(key);
}

/**
 * Display name for a key. Falls back to spacing the raw camel/Pascal key
 * ("FooBar24h" -> "Foo Bar 24h") so a label emitted by a newer platform build
 * than this webapp bundle still renders readably instead of blank.
 */
export function orderOverviewLabelName(key: string): string {
  const meta = LABELS_BY_KEY.get(key);
  if (meta) return meta.displayName;
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Za-z])(\d)/g, "$1 $2");
}

/**
 * Keys excluded from the `label_change` review trigger. Derived, so adding a
 * flapping label is a one-line catalog edit rather than a second hard-coded
 * Set that drifts.
 */
export const FLAPPING_ORDER_OVERVIEW_LABELS: readonly string[] =
  ORDER_OVERVIEW_LABELS.filter((label) => label.flaps).map(
    (label) => label.key,
  );

/**
 * Sort key for rendering a row's fired labels: most severe first, then motion,
 * then hygiene, with unknown keys last.
 */
export function orderOverviewLabelRank(key: string): number {
  const meta = LABELS_BY_KEY.get(key);
  if (!meta) return Number.MAX_SAFE_INTEGER;
  if (meta.severityRank != null) return meta.severityRank;
  return meta.kind === "motion" ? 1_000 : 2_000;
}

// ============================================================================
// Health
// ============================================================================

export const ORDER_HEALTH_VALUES = [
  "at_risk",
  "needs_attention",
  "on_track",
] as const;

export type OrderHealth = (typeof ORDER_HEALTH_VALUES)[number];

/** Severity-first — the tile order on the page AND the default list sort. */
export const ORDER_HEALTH_ORDER: readonly OrderHealth[] = ORDER_HEALTH_VALUES;

export interface OrderHealthMeta {
  label: string;
  /**
   * The rubric sentence. Read by BOTH the AI reviewer's system prompt
   * (interpolated in evaluate-orders.ts) and the UI tooltip, so the model and
   * the reader are held to the same definition.
   */
  definition: string;
}

export const ORDER_HEALTH_META: Record<OrderHealth, OrderHealthMeta> = {
  at_risk: {
    label: "At Risk",
    definition:
      "Verified real jeopardy: a customer escalation, property or health damage, financial exposure, or a deal being lost. Must be supported by evidence in the order's own thread — never from a fired label alone.",
  },
  needs_attention: {
    label: "Needs Attention",
    definition:
      "A concrete action is required and the next steps say specifically what. The default when an order is not clean but not burning.",
  },
  on_track: {
    label: "On Track",
    definition:
      "Progressing normally. Any fired labels have benign explanations — a scheduled visit, a prepaid plan, fresh intake, or cost booked before revenue — recorded as dismissed.",
  },
};

/**
 * Reason stamped on a fired label the AI reviewer neither confirmed nor
 * dismissed.
 *
 * Shared because BOTH sides need it: the platform writes it when reconciling a
 * verdict, and the webapp reads it to render those labels as a distinct
 * "awaiting judgment" state rather than burying them among genuine dismissals.
 * The label fired deterministically — only the judgment is missing — so hiding
 * it would let a real signal disappear from the report.
 */
export const UNADJUDICATED_LABEL_REASON = "not adjudicated by the reviewer";

export function isOrderHealth(value: string | undefined): value is OrderHealth {
  return (
    value != null && (ORDER_HEALTH_VALUES as readonly string[]).includes(value)
  );
}
